import styled from 'styled-components';
import { Flex, Typography } from 'antd';
import SbCard from 'uiCore/SbCard/SbCard';
import SbTag from 'uiCore/SbTag/SbTag';

const { Text } = Typography;

// ========== ORIGINAL ADMIN SETUP STYLES ==========
export const PageWrapperStyled = styled.div`
  padding: 32px 40px;
  background: ${p => p.theme?.colors?.light ?? '#f7f8fa'};
  min-height: 100%;
`;

export const PageLoaderStyled = styled(Flex)`
  min-height: 60vh;
`;

// Separator between a page header and its content (matches the JD Templates page)
export const SectionWrapperStyled = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionHeaderStyled = styled(Flex)`
  margin-bottom: 16px;
  gap: 8px;
`;

export const SectionTitleStyled = styled.span`
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 500;
`;

// ===== Shared SetupCard (one card used by every Admin Setup section) =====
export const SetupCardStyled = styled(SbCard)`
  height: 100%;

  .ant-card-body {
    padding: 18px;
    min-height: 200px;
    display: flex;
    flex-direction: column;
    position: relative;
  }
`;

export const CardBadgeWrapperStyled = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;

// Card badge — a solid primary-blue pill: blue background, white text, white tick.
export const CardTagStyled = styled(SbTag)`
  && {
    border-radius: 20px;
    color: ${p => p.theme?.colors?.white ?? '#ffffff'} !important;
    background: ${p => p.theme?.colors?.primary ?? '#0155e7'} !important;
    border-color: ${p => p.theme?.colors?.primary ?? '#0155e7'} !important;
  }
  && .anticon,
  && .anticon svg {
    color: ${p => p.theme?.colors?.white ?? '#ffffff'} !important;
    fill: ${p => p.theme?.colors?.white ?? '#ffffff'} !important;
  }
`;

export const CardTitleStyled = styled(Text)`
  display: block;
  margin-bottom: 4px;
`;

export const CardDescStyled = styled(Text)`
  display: block;
  margin-bottom: 12px;
  font-size: 13px;
`;

export const CardIconWrapperStyled = styled(Flex)`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${p => p.theme?.colors?.primary ?? '#0d7377'};
  color: white;
  font-size: 24px;
  margin-bottom: 12px;
`;

// ========== NEW ORGANIZATION CONTEXT STYLES (for modals) ==========

export const AssistCard = styled.div`
  background: linear-gradient(135deg, #0d7377 0%, #095456 100%);
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 12px rgba(13, 115, 119, 0.15);

  .avatar {
    font-size: 32px;
    flex-shrink: 0;
  }

  .content {
    flex: 1;

    .percentage {
      font-size: 18px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.white};
      margin: 0 0 4px 0;
    }

    .message {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.9);
      margin: 0;
      line-height: 1.5;
    }
  }
`;

export const UploadZone = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 32px 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #0d7377;
    background: #f9fafb;
  }

  i {
    font-size: 32px;
    color: ${({ theme }) => theme.colors.textLight};
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin: 8px 0 4px 0;
  }

  span {
    font-size: 12px;
    color: #6b7280;
  }
`;

export const TagInputContainer = styled.div`
  .tag-input-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    min-height: 40px;
    cursor: text;

    &:focus-within {
      border-color: #0d7377;
      box-shadow: 0 0 0 2px rgba(13, 115, 119, 0.1);
    }

    .tag-item {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: #f3f4f6;
      border: 1px solid ${({ theme }) => theme.colors.borderLight};
      border-radius: 4px;
      padding: 4px 8px;
      font-size: 13px;
      color: #374151;

      .remove-tag {
        cursor: pointer;
        color: #6b7280;
        font-weight: bold;
        font-size: 14px;
        line-height: 1;

        &:hover {
          color: ${({ theme }) => theme.colors.danger};
        }
      }
    }

    input {
      flex: 1;
      min-width: 120px;
      border: none;
      outline: none;
      font-size: 14px;
      padding: 4px;
    }
  }

  .tag-counter {
    font-size: 12px;
    color: #6b7280;
    margin-top: 4px;
  }
`;

export const CharacterCounter = styled.div`
  text-align: right;
  font-size: 12px;
  color: ${({ theme, overLimit }) => overLimit ? theme.colors.danger : '#6b7280'};
  margin-top: 4px;
`;

export const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px;
  text-align: center;

  .error-icon {
    font-size: 48px;
    color: ${({ theme }) => theme.colors.danger};
    margin-bottom: 16px;
  }

  h3 {
    font-size: 20px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 8px 0;
  }

  p {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
  }
`;

export const SkeletonCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 14px;
  padding: 16px 18px;
  min-height: 100px;
  display: flex;
  gap: 12px;

  .skeleton-bar {
    width: 4px;
    height: 60px;
    background: linear-gradient(90deg, #f3f4f6 25%, ${({ theme }) => theme.colors.borderLight} 50%, #f3f4f6 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s ease-in-out infinite;
    border-radius: 4px;
  }

  .skeleton-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .skeleton-line {
      height: 12px;
      background: linear-gradient(90deg, #f3f4f6 25%, ${({ theme }) => theme.colors.borderLight} 50%, #f3f4f6 75%);
      background-size: 200% 100%;
      animation: skeleton-loading 1.5s ease-in-out infinite;
      border-radius: 4px;

      &.title {
        width: 70%;
        height: 16px;
      }

      &.subtitle {
        width: 90%;
      }

      &.helper {
        width: 60%;
      }
    }
  }

  @keyframes skeleton-loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// ========== MODAL COMMON STYLES ==========

export const ModalHeaderText = styled.span`
  display: block;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

export const ModalDescription = styled.span`
  display: block;
  margin-bottom: 20px;
  font-size: 13px;
  color: #6b7280;
`;

export const FormatGuideBox = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  font-size: 12px;
  color: #6b7280;

  .guide-title {
    display: block;
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
  }

  .guide-item {
    display: block;
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }

    code {
      padding: 2px 6px;
      background: ${({ theme }) => theme.colors.borderLight};
      border-radius: 3px;
      font-family: 'Courier New', monospace;
      font-size: 11px;
      color: #374151;
    }
  }
`;

export const RichTextArea = styled.div`
  .ant-input {
    font-family: 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.6;
  }
`;

export const CharCountDisplay = styled.div`
  font-size: 12px;
  color: ${props => props.overLimit ? '#ef4444' : '#6b7280'};
  text-align: right;
  margin-top: 8px;

  .warning {
    margin-left: 8px;
    color: #f59e0b;
  }
`;
